<template>
    <main>
        <div class="only-tip">
            {{ $t('app_free_cloud.for_this_camera') }}
        </div>
        <h1>
            {{ $t('app_free_cloud.free_cloud_plan') }}<span>*</span>
        </h1>
        <ElCarousel  height="249px"
            indicator-position="outside"
            class="carousel"
            :autoplay="false"
            arrow="never"
        >
            <ElCarouselItem v-for="img in imgs" :key="img">
                <div>
                    <img :src="img" alt="img"/>
                </div>
            </ElCarouselItem>
        </ElCarousel>
        <div class="points">
            <div v-for="point in points" :key="point" class="point">
                <span class="icon-co_success_blue">
                    <span class="path1"></span>
                    <span class="path2"></span>
                </span>
                <span v-html="point"/>
            </div>
        </div>
        <div class="button" @click="handleGetFree">
            <reo-common-button
                :label="$t('app_free_cloud.button_text')"
                customWidth="100%"
            />
            <div class="free">{{ $t('plan.free') }}</div>
        </div>
        <section class="benefits">
            <h2>{{ $t('app_free_cloud.benefits_title') }}</h2>
            <ul>
                <li v-for="benefit in $t( 'app_free_cloud.benefits' )" :key="String(benefit)"><span class="text">{{ benefit }}</span></li>
            </ul>
        </section>
        <ul class="notes">
            <li v-for="note in $t('app_free_cloud.notes')" :key="String(note)">
                <span class="text">{{ note }}</span>
            </li>
        </ul>

    </main>

</template>

<script lang="ts">
import nProgress from 'nprogress';
import Vue from 'vue';
import { Carousel as ElCarousel, CarouselItem as ElCarouselItem } from 'element-ui';
import img1 from 'IMAGE/appFreePlan/1.webp';
import img2 from 'IMAGE/appFreePlan/2.webp';
import { IPlan } from '@reolink-cloud/interface';
import { productsManager, uidHandler } from '@/lib';
import { mapGetters } from 'vuex';
import { getQueryParam, requestWithErrorHandler } from '@/common/js/utils';
import { ESubscribeMode } from '@/interface';
import { getFreeOrderPlan } from '@/api/order';
import { callAppApi } from '@reolink-web/app-sdk';
import { getDeviceList } from '@/api/cloud';
import { unfoldError } from '@/common/js/notice';
import { config } from '@/config/reolink';
import reolinkGa from '@/common/js/gtagPageview';

export default Vue.extend({
    components:{
        ElCarousel,
        ElCarouselItem
    },
    data() {

        return {
            imgs: [img1, img2],
            plan: null as null | IPlan,
            selectedCountry: ''
        };

    },
    computed:{
        points(): string[] {

            const result: string[] = [];
            if ( this.plan?.style['cloud_storage.video_lifetime'] ) {

                result.push( this.$tc( 'app_free_cloud.storage_history', undefined, { count: this.plan?.style['cloud_storage.video_lifetime'] }), );

            }
            if ( this.plan?.style['cloud_storage.allow_rich_notification'] ) {

                result.push( this.$tc( 'app_free_cloud.rich_notification' ));

            }

            return result;

        },
        uid(): string {

            const ymh = this.$route.query.ymh;
            if ( typeof ymh === 'string' ) {

                return uidHandler.decrypt( this.$route.query.ymh as string );

            }

            return '';

        },
        ...mapGetters( ['lang', 'country', 'userId'] as const )
    },
    methods:{
        async getFreePlan() {

            await productsManager.update({  lang: this.lang });
            this.selectedCountry = productsManager.getDefaultCountryKey( getQueryParam( this.$route, 'country', this.country )[0] ) ?? config.fallbackCountry;
            this.plan = ( await getFreeOrderPlan({
                region: this.selectedCountry,
                lang: this.lang,
                model: this.$route.query.model as string
            })).plan;

        },
        async handleGetFree() {

            reolinkGa.sendFreeOfferEvent( 'cloud-reco1-single-60day-plan-get' );

            const url = new URL( '/checkout', location.origin );
            url.searchParams.set( 'country', this.selectedCountry );
            url.searchParams.set( 'p_id', this.plan?.id ?? '' );
            url.searchParams.set( 'freeOrder', '1' );
            url.searchParams.set( 'subNew', '1' );
            url.searchParams.set( 'mode', ESubscribeMode.CREATE );
            if ( !this.userId ) {

                url.searchParams.set( 'inDoorbellProgressUid', this.uid as string );
                await callAppApi( 'login', url.href );
                return;

            } else {

                url.searchParams.set( 'inDoorbellProgress', this.$route.query.ymh as string );

                await requestWithErrorHandler( async () => {

                    const deviceList = await getDeviceList();
                    const emailVerified = this.$store.state.user.user.email.status === 'verified';
                    const deviceBind = deviceList.some( device => device.uid === this.uid && device.status === 'bound' );
                    if ( emailVerified && deviceBind ) {

                        await this.$router.push( url.pathname  + url.search );

                    } else if ( !emailVerified ) {

                        unfoldError( 'cloud.noVerify.notice_verify_email' );

                    } else {

                        await callAppApi( 'bindDevice', url.href );

                    }

                });

            }

        }
    },
    async created() {

        await requestWithErrorHandler( this.getFreePlan );

    },
    mounted() {

        nProgress.done();

    }
});
</script>

<style lang="less" scoped>
main {
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    background-size: 100% 187px;
    background-repeat: no-repeat;
    background-image: linear-gradient(180deg, #C1E1FF 0%, rgba(218, 233, 247, 0) 100%);

    h1 {
        font-size: 20px;
        font-weight: 600;
        line-height: 30px;
        text-align: center;
        span{
            font-weight: 550;
        }
    }

    .only-tip {
        border: 1px solid #5D96FF;
        box-shadow: 5px 5px 0px 0px #5D96FF;
        border-radius: 4px;
        background: @white;
        padding: 4px 14px;
        margin-bottom: 2px;
        color: @b_6;
        font-size: 14px;
        font-weight: 500;
        line-height: 21px;
    }
    .carousel{
        width: 222px;
        img{
            width: 100%;
        }
        /deep/ .el-carousel__indicator{
            padding: 0 4px;
            .el-carousel__button{
                width: 6px;
                height: 6px;
                border-radius: 100%;
                background-color: black;
            }
        }
    }
    .points{
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 0 9px;
        width: 100%;
        .point{
            font-size: 14px;
            font-weight: 500;
            line-height: 16px;
            color: @b_6;
        }
    }
    .button{
        width: 100%;
        position: relative;
        .free{
            position: absolute;
            top: 0;
            right: 0;
            border-radius: 0px 8px 0px 8px;
            background-color: #FF742A;
            padding: 2px 10px;
            color: @white;
            font-size: 11px;
            font-weight: 600;
            line-height: 14px;
            border: 1px solid #FF742A;
        }
    }
    .benefits{
        padding: 12px 16px 24px 16px;
        background: linear-gradient(180deg, #ECF5FF 0%, rgba(235, 245, 255, 0) 100%);
        width: 100%;
        h2{
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 12px;
        }
        ul{
            display: flex;
            flex-direction: column;
            gap: 12px;
            li{
                font-size: 12px;
                font-weight: 500;
                line-height: 16px;
                color: @b_6;
                list-style-type: disc;
                list-style-position: inside;
                .text{
                    margin-left: -8px;
                }
            }
        }
    }

    ul.notes{
        display: flex;
        flex-direction: column;
        gap: 6px;
        width: 100%;
        margin-top: -6px;

        li{
            font-size: 11px;
            font-weight: 500;
            line-height: 16px;
            color: @font_secondary;
        }
    }
}
</style>
